/**
 * MachineScheduleDayCells.tsx
 * The per-row background layer of the Machine Schedule grid: one cell per day,
 * tinted to mark today / stat holidays / weekends so non-work days read as
 * "lab closed" even when a multi-day booking bar spans them.
 *
 * Extracted from MachineScheduleGrid.tsx to keep that file under the 600-line
 * cap (CODING-STANDARDS.md). Pure presentational — all colors are theme tokens
 * resolved by the parent and passed in (no hex literals here).
 *
 * Tint precedence (highest first): hovered column → today → holiday → weekend.
 */

import React from 'react';
import { Box } from '@mui/material';
import { isToday, isWeekend } from './scheduleGridShared';

export interface MachineScheduleDayCellsProps {
  /** ISO dates ('YYYY-MM-DD') for the visible window, in order. */
  days: string[];
  /** Column index currently hovered (cross-highlight), or null. */
  hoverCol: number | null;
  /** Day column width in px. */
  dayColWidth: number;
  /** ISO date → stat-holiday name, for the per-cell tooltip hint. */
  holidayNameByDate: Map<string, string>;
  /** Resolved tint tokens + border styles from the parent theme. */
  colors: {
    borderColor: string;
    todayBorder: string;
    colHighlight: string;
    todayBg: string;
    holidayBg: string;
    weekendBg: string;
  };
}

export const MachineScheduleDayCells: React.FC<MachineScheduleDayCellsProps> = ({
  days,
  hoverCol,
  dayColWidth,
  holidayNameByDate,
  colors,
}) => (
  <>
    {days.map((day, i) => {
      const holidayName = holidayNameByDate.get(day);
      return (
        <Box
          key={day}
          title={holidayName ? `${holidayName} (lab closed)` : undefined}
          sx={{
            // FIXED width (not flex-stretch): true Excel-like zoom — shrinking
            // the day width must visibly shrink the columns even when the grid
            // is narrower than its container (flex:1 used to stretch cells so
            // zoom-out looked like it "did nothing").
            width: dayColWidth,
            minWidth: dayColWidth,
            flexShrink: 0,
            height: '100%',
            borderRight: `1px solid ${colors.borderColor}`,
            borderLeft: isToday(day) ? colors.todayBorder : undefined,
            backgroundColor:
              i === hoverCol
                ? colors.colHighlight
                : isToday(day)
                  ? colors.todayBg
                  : holidayName
                    ? colors.holidayBg
                    : isWeekend(day)
                      ? colors.weekendBg
                      : 'transparent',
          }}
        />
      );
    })}
  </>
);
